import client from "@/api/client"
import { SDModel } from "@/api/sdk"
import { ComfyUINodeInfos } from "@/components/ComfyUI/types/node"
import { queryClient } from "@/providers/QueryProvider"
import { createAuthenticatedQuery } from "@/queries/createAuthenticatedQuery"
import { useCallback, useEffect, useMemo, useState } from "react"
import { createQuery } from "react-query-kit"

type ComfyModulePreset = {
  name: string
  enabledModules: string[]
}

const lastUpdateCacheKey = "gaia:comfyui:node_infos_update"
const nodeInfoCacheKey = "gaia:comfyui:node_infos"
const nodeI18nCacheKey = "gaia:comfyui:node_i18n"
const moduleFiltersKey = "gaia:comfyui:module_filters" // Stores the current non-preset filter list
const presetStorageKey = "gaia:comfyui:module_presets" // Stores array of ComfyModulePreset
const activePresetStorageKey = "gaia:comfyui:active_module_preset" // Stores name of active preset

export const useComfyUiNodeInfosQuery = createAuthenticatedQuery<ComfyUINodeInfos, {}, Error>({
  primaryKey: "comfyui-node-infos",
  queryFn: async () => {
    const lastUpdate = await client.api.comfyUiControllerGetNodeInfosLastUpdate().then(res => res.data)

    const lastUpdateCache = Number(localStorage.getItem(lastUpdateCacheKey))
    if (isNaN(lastUpdateCache) || lastUpdateCache < lastUpdate || !localStorage.getItem(nodeInfoCacheKey)) {
      const data = await client.api.comfyUiControllerGetNodeInfos().then(res => res.data)
      localStorage.setItem(nodeInfoCacheKey, JSON.stringify(data))
      localStorage.setItem(lastUpdateCacheKey, String(lastUpdate))
    }

    const rawData = JSON.parse(localStorage.getItem(nodeInfoCacheKey)!) as ComfyUINodeInfos
    return rawData
  },
  refetchOnWindowFocus: false,
})

export const useComfyUiNodeI18nQuery = createAuthenticatedQuery<Record<string, any>, {}, Error>({
  primaryKey: "comfyui-node-i18n",
  queryFn: async () => {
    const lastUpdate = await client.api.comfyUiControllerGetNodeInfosLastUpdate().then(res => res.data)

    const lastUpdateCache = Number(localStorage.getItem(lastUpdateCacheKey))
    if (isNaN(lastUpdateCache) || lastUpdateCache < lastUpdate || !localStorage.getItem(nodeI18nCacheKey)) {
      const data = await client.api.comfyUiControllerGetNodeI18N().then(res => res.data)
      localStorage.setItem(nodeI18nCacheKey, JSON.stringify(data))
      localStorage.setItem(lastUpdateCacheKey, String(lastUpdate))
    }

    return JSON.parse(localStorage.getItem(nodeI18nCacheKey)!)
  },
  refetchOnWindowFocus: false,
})

export const useComfyUiModelsQuery = createAuthenticatedQuery<
  Pick<SDModel, "id" | "fileName" | "variant">[],
  {},
  Error
>({
  primaryKey: "comfyui-models",
  queryFn: async () => {
    const modelNodeInfos = await client.api.comfyUiControllerGetModels().then(res => res.data)
    return modelNodeInfos
  },
  refetchOnWindowFocus: false,
})

export const filteredComfyUiNodeInfosQuery = createQuery({
  primaryKey: "comfyui-node-infos-filtered",
  queryFn: async () => {
    const queryKey = useComfyUiNodeInfosQuery.getKey()
    let data = queryClient.getQueryData(queryKey) as ComfyUINodeInfos | undefined
    if (!data) {
      // Fetch raw data if not in cache
      data = await useComfyUiNodeInfosQuery.queryFn(null as any)
    }

    // Get filters directly from the primary filter storage
    const storedFilters = localStorage.getItem(moduleFiltersKey)
    const enabledModules = storedFilters ? JSON.parse(storedFilters) : null

    if (enabledModules && data) {
      const filteredData: ComfyUINodeInfos = {}
      Object.entries(data).forEach(([key, node]) => {
        if (node.python_module && enabledModules.includes(node.python_module)) {
          filteredData[key] = node
        }
      })
      return filteredData
    }

    return data // Return all if no filters found
  },
  refetchOnWindowFocus: false, // Don't refetch just on focus
})

export const useComfyModuleFilters = () => {
  const { data: nodeInfos } = useComfyUiNodeInfosQuery({ enabled: true })

  const [enabledModules, setEnabledModules] = useState<string[]>()
  const [presets, setPresets] = useState<ComfyModulePreset[]>([])
  const [activePresetName, setActivePresetName] = useState<string | null>(null)

  const allModules = useMemo(() => {
    if (!nodeInfos) return []
    const modules = new Set<string>()
    Object.values(nodeInfos as ComfyUINodeInfos).forEach(nodeInfo => {
      if (nodeInfo.python_module) {
        modules.add(nodeInfo.python_module)
      }
    })
    return Array.from(modules).sort()
  }, [nodeInfos])

  const getEnabledModulesFromStorage = useCallback(() => {
    const storedFilters = localStorage.getItem(moduleFiltersKey)
    return storedFilters ? JSON.parse(storedFilters) : null
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return

    const savedPresets = localStorage.getItem(presetStorageKey)
    if (savedPresets) {
      setPresets(JSON.parse(savedPresets))
    }

    const savedActivePreset = localStorage.getItem(activePresetStorageKey)
    setActivePresetName(savedActivePreset)
  }, [])

  // Initialize enabledModules state based on active preset or stored filters
  useEffect(() => {
    if (typeof window === "undefined" || !allModules?.length) return

    let initialModules: string[] | null = null

    // 1. Try active preset
    if (activePresetName) {
      const activePreset = presets.find(p => p.name === activePresetName)
      if (activePreset) {
        initialModules = activePreset.enabledModules
      }
    }

    // 2. Try general stored filters if no active preset worked
    if (initialModules === null) {
      initialModules = getEnabledModulesFromStorage()
    }

    // 3. Default to all enabled if nothing else worked
    if (initialModules === null) {
      initialModules = allModules
    }

    setEnabledModules(initialModules)
  }, [allModules, activePresetName, presets, getEnabledModulesFromStorage])

  // Sync enabledModules to primary storage and invalidate filtered query
  useEffect(() => {
    if (enabledModules && typeof window !== "undefined") {
      localStorage.setItem(moduleFiltersKey, JSON.stringify(enabledModules))
      // If the current enabled list doesn't match any preset, clear the active preset name
      const matchingPreset = presets.find(
        p => JSON.stringify(p.enabledModules.sort()) === JSON.stringify(enabledModules.sort()),
      )
      if (!matchingPreset && activePresetName) {
        setActivePresetName(null)
        localStorage.removeItem(activePresetStorageKey)
      }
      // Always invalidate the filtered query when the underlying filter changes
      queryClient.invalidateQueries({ queryKey: filteredComfyUiNodeInfosQuery.getKey() })
    }
  }, [enabledModules, presets, activePresetName])

  const savePreset = useCallback(
    (name: string) => {
      if (!name || !enabledModules) return
      const newPreset: ComfyModulePreset = { name, enabledModules }
      setPresets(prevPresets => {
        const existingIndex = prevPresets.findIndex(p => p.name === name)
        let updatedPresets = [...prevPresets]
        if (existingIndex > -1) {
          updatedPresets[existingIndex] = newPreset // Overwrite
        } else {
          updatedPresets.push(newPreset) // Add new
        }
        localStorage.setItem(presetStorageKey, JSON.stringify(updatedPresets))
        return updatedPresets
      })
      // Optionally make the saved preset active immediately
      // applyPreset(name)
    },
    [enabledModules],
  )

  const applyPreset = useCallback(
    (name: string) => {
      const presetToApply = presets.find(p => p.name === name)
      if (presetToApply) {
        setEnabledModules(presetToApply.enabledModules)
        setActivePresetName(name)
        localStorage.setItem(activePresetStorageKey, name)
      }
    },
    [presets],
  )

  const deletePreset = useCallback(
    (name: string) => {
      setPresets(prevPresets => {
        const updatedPresets = prevPresets.filter(p => p.name !== name)
        localStorage.setItem(presetStorageKey, JSON.stringify(updatedPresets))
        return updatedPresets
      })
      if (activePresetName === name) {
        setActivePresetName(null)
        localStorage.removeItem(activePresetStorageKey)
        // Optionally revert to default/all enabled or last non-preset state?
        // For now, it keeps the modules enabled but marks as 'custom'
      }
    },
    [activePresetName],
  )

  const clearActivePreset = useCallback(() => {
    setActivePresetName(null)
    localStorage.removeItem(activePresetStorageKey)
    // Re-apply filters from the general storage key
    const fallbackFilters = getEnabledModulesFromStorage()
    setEnabledModules(fallbackFilters ?? allModules)
  }, [getEnabledModulesFromStorage, allModules])

  const toggleModule = useCallback((module: string) => {
    setEnabledModules(prev => {
      if (!prev) return [module]
      const updated = prev.includes(module) ? prev.filter(m => m !== module) : [...prev, module]
      // Clear active preset name since we've deviated
      setActivePresetName(null)
      localStorage.removeItem(activePresetStorageKey)
      return updated
    })
  }, [])

  const toggleAllModules = useCallback(
    (enabled: boolean) => {
      const targetModules = enabled ? allModules : []
      setEnabledModules(targetModules)
      // Clear active preset name since we've deviated
      setActivePresetName(null)
      localStorage.removeItem(activePresetStorageKey)
    },
    [allModules],
  )

  return {
    allModules,
    enabledModules,
    toggleModule,
    toggleAllModules,
    presets,
    activePresetName,
    savePreset,
    applyPreset,
    deletePreset,
    clearActivePreset,
  }
}
